import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { GET_INDICATOR_ALERTS } from "./realQueries";
import { ActiveIndicator } from "./types";

interface RawIndicatorEvent {
  deviceEsn: string;
  eventTime?: string | null;
  activeIndicators: ActiveIndicator[];
}

const LOOKBACK_HOURS = 24;

// Only fires while the Indicators tab is mounted (see IndicatorsPage) — this hook is not
// called from the dashboard shell, so switching tabs is what triggers/cancels this query.
export function useVehicleIndicators(deviceEsns: string[]) {
  const startDate = useMemo(() => DateTime.now().minus({ hours: LOOKBACK_HOURS }).toUTC().toISO(), []);
  const endDate = useMemo(() => DateTime.now().toUTC().toISO(), []);

  const { data, loading, error, refetch } = useQuery<{ telematicIndicatorEvents: RawIndicatorEvent[] }>(GET_INDICATOR_ALERTS, {
    variables: { deviceEsn: deviceEsns, startDate, endDate },
    skip: deviceEsns.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const indicatorsByEsn = useMemo(() => {
    const grouped: Record<string, ActiveIndicator[]> = {};
    (data?.telematicIndicatorEvents ?? []).forEach((event) => {
      grouped[event.deviceEsn] = event.activeIndicators ?? [];
    });
    return grouped;
  }, [data]);

  return { indicatorsByEsn, loading, error, refetch };
}
